import React from 'react';
import classnames from 'classnames';
import * as mobxReact from 'mobx-react';
import '../list-popup/attendees-section.scss';
import { OrganizationMultiSelectButton } from '../../../../global/multi-select/organization-multi-select-button';
import { MultiSelectFilterType } from '../../../../global/multi-select/make-multi-select-in-popover';
import { AttendeeRole, IAttendeeInput, IAttendeeOrganizationDTO, IControlAttendeeDetailsDTO, IFilteredOrganization } from '../../../../../api/attendees.dao';
import { IMeetingAttendeeInput } from '../../../../../api/control.dao';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { AttendeesList } from './_attendees-list';
import {RUN_MEETING_EVENTS} from '../../../../../tracking-service/tracking-values';
import { addText, cancelText } from '../../../../../constants/common-labels.constants';

interface IProps {
    name: string;
    id?: string;
    role: AttendeeRole;
    roleKey: string;
    attendees: IAttendeeOrganizationDTO[];
    selected: IFilteredOrganization[];
    disabled: Record<number, Record<number, boolean>>;
    updateAttendee: (attendeeInput: IAttendeeInput, roleKey: string, organizationId: number, attendeeId: number) => void;
    addMeetingAttendees: (walkInsInput: IMeetingAttendeeInput[]) => void;
    fetchAttendeeData: (attendeeId: number) => Promise<IControlAttendeeDetailsDTO>;
    eventTimezone: string;
    intl: IntlShape;
    isExpanded?: boolean;
    removeWalkIn: (roleKey: string, role: AttendeeRole, organizationId: number, attendeeId: number) => void;
    ActionButton?: React.FunctionComponent;
    attendeeIdToConnectionNameMap?: { [id: string] : string };
    orgIdToConnectionParticipantsNameMap?: { [id: string] : string };
    orgIdToConnectionPresentersNameMap?: { [id: string] : string };
    orgIdToConnectionHostsNameMap?: { [id: string] : string };
    orgIdToConnectionSupportsNameMap?: { [id: string] : string };
    onClick?: () => void;
    onEditAttendee: (attendeeId: number) => void;
    selectedAttendeeFilter?: boolean;
    isAttendeeListSortedByFirstName?: boolean;
    isWalkIn?: boolean;
    isMeetingUnderway: boolean;
    handleTrackingEvent?: (event: RUN_MEETING_EVENTS, value?: string) => void;
    loadingAttendees: boolean;
    resetLoadingState: () => void;
}

const AttendeesSection = mobxReact.observer(injectIntl((props: IProps) => {
    const {
        name,
        disabled,
        attendees,
        selected: selectedOrganizations,
        updateAttendee,
        addMeetingAttendees,
        fetchAttendeeData,
        eventTimezone,
        role,
        id,
        intl,
        isExpanded,
        roleKey,
        removeWalkIn,
        ActionButton,
        attendeeIdToConnectionNameMap,
        orgIdToConnectionParticipantsNameMap,
        orgIdToConnectionPresentersNameMap,
        orgIdToConnectionHostsNameMap,
        orgIdToConnectionSupportsNameMap,
        onClick,
        onEditAttendee,
        selectedAttendeeFilter,
        isAttendeeListSortedByFirstName,
        isWalkIn,
        isMeetingUnderway,
        handleTrackingEvent,
        loadingAttendees,
        resetLoadingState,
    } = props;

    const addWalkInButtonText = `${intl.formatMessage(
        {
            id: 'oe.control.addWalkIn',
            description: 'Add Walk-in button that add some organization or attendee for the section',
            defaultMessage: '{prefix}Add Walk-in',
        },
        {
            prefix: '+ '
        }
        )} ${isExpanded ? intl.formatMessage(
            {
                id: id,
                description: 'Role name in Add Walk-in button',
                defaultMessage: name,
            },
        ) : ''}`;

    const addWalkInAttendees = (allCheckedOrganizations: IAttendeeOrganizationDTO[]) => {
        const walkInOrganizations = allCheckedOrganizations.filter(el => !selectedOrganizations.includes(el));

        const attendeesForUpdate = walkInOrganizations.map(walkInOrganization => {
            const { organizationId: walkInOrgId, attendees: walkInOrgAttendees } = walkInOrganization;

            const existingOrganization = selectedOrganizations.find(el => el.organizationId === walkInOrgId);
            const walkInAttendees = walkInOrgAttendees.filter(walkInOrgAttendee => {
                if (existingOrganization) {
                    const isAttendeeIncludesInExistingOrganization = existingOrganization.attendees.includes(walkInOrgAttendee);
                    return !isAttendeeIncludesInExistingOrganization;
                }

                return true;
            });

            return {
                organizationId: walkInOrgId.toString(),
                role: role,
                attendeeIds: walkInAttendees.map(el => el.attendeeId),
            };
        });

        if (attendeesForUpdate.length) {
            addMeetingAttendees(attendeesForUpdate);
        }
    };

    return (
        <section className={classnames('attendee-section', {'attendee-section--with-table': isExpanded})}>
            <label className='attendee-section__label'>
                {
                    id ? <FormattedMessage
                        id={id}
                        defaultMessage={name}
                        description='Popup sub-header label text'
                    /> : name
                }
            </label>
            <AttendeesList
                onChangeAttendee={updateAttendee}
                organizations={selectedOrganizations}
                fetchAttendeeData={fetchAttendeeData}
                eventTimezone={eventTimezone}
                withTableView={isExpanded}
                roleKey={roleKey}
                removeWalkIn={(organizationId, attendeeId) => removeWalkIn(roleKey, role, organizationId, attendeeId)}
                attendeeIdToConnectionNameMap={attendeeIdToConnectionNameMap}
                orgIdToConnectionParticipantsNameMap={orgIdToConnectionParticipantsNameMap}
                orgIdToConnectionPresentersNameMap={orgIdToConnectionPresentersNameMap}
                orgIdToConnectionHostsNameMap={orgIdToConnectionHostsNameMap}
                orgIdToConnectionSupportsNameMap={orgIdToConnectionSupportsNameMap}
                onEditAttendee={onEditAttendee}
                selectedAttendeeFilter={selectedAttendeeFilter}
                isAttendeeListSortedByFirstName={isAttendeeListSortedByFirstName}
                isMeetingUnderway={isMeetingUnderway}
                handleTrackingEvent={handleTrackingEvent}
            />
            <OrganizationMultiSelectButton
                className='attendee-list__add-button'
                items={attendees}
                value={selectedOrganizations}
                disabled={disabled}
                keyProperty='organizationId'
                nameProperty='name'
                onChange={addWalkInAttendees}
                onClick={() => onClick?.()}
                text={addWalkInButtonText}
                filterType={MultiSelectFilterType.Group}
                applyButtonText={addText()}
                resetButtonText={cancelText()}
                searchPlaceholder={
                    intl.formatMessage(
                        {
                            id: 'oe.organization.searchOrganization',
                            description: 'Organization search input placeholder',
                            defaultMessage: 'Search',
                        }
                    )
                }
                position={isExpanded ? 'right' : null}
                align={isExpanded ? 'center' : null}
                isWide={isExpanded}
                ActionButton={ActionButton}
                isWalkIn={isWalkIn}
                loadingAttendees={loadingAttendees}
                resetLoadingState={resetLoadingState}
            />
        </section>
    );
}));

export { AttendeesSection };
